import { parseTimeToHMS, parseBooleanCell, splitMultiValue, coerceCustomFieldValue } from './fmxFieldTypes';
import { ASSET_CONDITION_MAP, ASSET_CATEGORY_MAP, getCustomFieldsPayloadKey } from './fmxFieldMetadata';
import { FMX_ASSIGNMENT_FIELDS } from './fmxEndpoints';
import { getBaseSchemaType, getSchemaModuleSlug } from './schemas';
import { fmxFetch } from './apiClient';
import { fetchAllPages } from './fmxSync';

function generateDefaultPassword() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%';
  let pw = '';
  for (let i = 0; i < 16; i++) pw += chars[Math.floor(Math.random() * chars.length)];
  return pw;
}

// coerceCustomFieldValue moved to fmxFieldTypes.js (shared with the
// equipment attachment scan writer and node-testable there).

function coerceSystemFieldValue(value, fieldType) {
  if (value === null || value === undefined || value === '') return value;
  switch (fieldType) {
    case 'number': {
      const cleaned = String(value).replace(/[^0-9.-]/g, '');
      const num = parseFloat(cleaned);
      return isNaN(num) ? value : num;
    }
    case 'date': {
      const d = new Date(value);
      return isNaN(d.getTime()) ? value : d.toISOString();
    }
    case 'boolean': {
      // The FMX API's boolean converter only accepts true/false; unrecognized
      // cells pass through raw so the row fails visibly at the API.
      const b = parseBooleanCell(value);
      return b === undefined ? value : b;
    }
    case 'stringArray':
      return splitMultiValue(value);
    case 'time': {
      // FMX time fields (e.g. building operating hours) are .NET TimeSpans and
      // only accept "HH:mm:ss"; unparseable values pass through so the row
      // fails visibly rather than silently dropping the field.
      const t = parseTimeToHMS(value);
      return t ?? value;
    }
    default:
      return value;
  }
}

// --- Per-schema payload finalizers ---
// Applied as the last step of transformRowToPayload, after dot-notation
// composition and lookup resolution. Keep payload reshaping that can't be
// expressed per-field here so both the bulk push and per-row retry share it.

// UtilityProviderEditModel is { name, repeatInterval, services: [...] } — the
// per-service fields map onto UtilityServiceEditModel and must be nested.
const UTILITY_SERVICE_FIELDS = [
  'utilityTypeID',
  'isConsumptionTracked', 'consumptionUnit', 'isConsumptionSplitByMeter',
  'isDemandTracked', 'demandUnit', 'isDemandSplitByMeter',
  'isEnergyUseIntensityTracked', 'kbtuConversionFactor',
  'isWaterUseIntensityTracked', 'gallonConversionFactor',
];

const PAYLOAD_FINALIZERS = {
  'Utility Provider': (payload) => {
    const service = {};
    for (const key of UTILITY_SERVICE_FIELDS) {
      if (payload[key] !== undefined) {
        service[key] = payload[key];
        delete payload[key];
      }
    }
    if (Object.keys(service).length > 0) payload.services = [service];
  },
  'Schedule Request': (payload) => {
    // ScheduleEditModel needs `terminal` to say how the recurrence ends; derive
    // it from whichever end field was mapped unless the user mapped it directly.
    const schedule = payload.schedule;
    if (!schedule || typeof schedule !== 'object' || schedule.terminal) return;
    if (schedule.terminalOccurrenceCount !== undefined) {
      schedule.terminal = 'After a certain number of occurrences';
    } else if (schedule.terminalEndDate !== undefined) {
      schedule.terminal = 'On date';
    }
  },
};

// Transform a mapped row object into the correct FMX API payload shape.
// idCache: { "Building:Main Campus": 42 }
// customFieldIdMap: { "Year Built": 42, "Region": 7 } — maps friendly field name to FMX custom field ID
// customFieldMetadata: [{ id: 42, name: "Year Built", fieldType: "Numeric" }]
export function transformRowToPayload(row, schemaType, idCache = {}, customFieldIdMap = {}, customFieldMetadata = [], fieldMapOverride = null, lookupFieldsOverride = null, fieldTypeMap = {}) {
  const baseType = getBaseSchemaType(schemaType);
  const fieldMap = fieldMapOverride || {};
  const payload = {};
  const customFields = [];
  const droppedFields = [];

  // Build a set of lookup field names so we don't report them as "dropped"
  const effectiveLookups = lookupFieldsOverride || {};
  const lookupFieldNames = new Set(Object.keys(effectiveLookups));

  // Assignment fields never enter the main payload — StepExport reads them off
  // the row and posts them to the assignments sub-endpoint after the create.
  const assignmentFields = FMX_ASSIGNMENT_FIELDS[baseType];
  const assignmentFieldNames = new Set(
    assignmentFields ? [assignmentFields.userField, assignmentFields.priorityField] : []
  );

  Object.entries(row).forEach(([fieldName, value]) => {
    if (value === null || value === undefined || value === '') return;

    if (assignmentFieldNames.has(fieldName)) return;

    // Match by friendly name in customFieldIdMap (e.g. "Year Built" → ID 42)
    if (customFieldIdMap[fieldName] !== undefined) {
      const cfId = customFieldIdMap[fieldName];
      const cfMeta = customFieldMetadata.find(cf => cf.id === cfId);

      // Multi-select dropdown: split delimited value into array
      if (cfMeta?.allowMultipleSelections) {
        const parts = String(value).split(/[;,]/).map(s => s.trim()).filter(Boolean);
        if (parts.length > 0) {
          customFields.push({ customFieldID: cfId, values: parts });
        }
      } else {
        const coerced = coerceCustomFieldValue(value, cfMeta?.fieldType);
        if (coerced !== null) {
          customFields.push({ customFieldID: cfId, value: coerced });
        }
      }
      return;
    }

    // Match by legacy key format "customField_42"
    if (fieldName.startsWith('customField_')) {
      const id = parseInt(fieldName.replace('customField_', ''), 10);
      if (!isNaN(id)) {
        const cfMeta = customFieldMetadata.find(cf => cf.id === id);
        const coerced = coerceCustomFieldValue(value, cfMeta?.fieldType);
        if (coerced !== null) {
          customFields.push({ customFieldID: id, value: coerced });
        }
      }
      return;
    }

    // Special handling: Equipment Asset Condition → integer enum
    // (FilterAssetCondition: NotSpecified=1 … Excellent=6). Unmappable values
    // (including 'retired' — not a condition in FMX) are omitted rather than
    // sent as an invalid enum value, which would 400 the whole row.
    if (baseType === 'Equipment' && fieldName === 'Asset Condition') {
      const normalized = String(value).toLowerCase().trim();
      const enumVal = ASSET_CONDITION_MAP[normalized];
      if (enumVal !== undefined) {
        payload['assetCondition'] = enumVal;
      } else {
        const parsed = parseInt(value, 10);
        if (!isNaN(parsed) && parsed >= 1 && parsed <= 6) {
          payload['assetCondition'] = parsed;
        } else {
          console.warn(`[FMX Transform] Unsupported Asset Condition "${value}" — field omitted`);
        }
      }
      return;
    }

    // Special handling: Equipment Asset Category → integer enum
    // (AssetCategory: PackagedRooftopUnit=0 … SumpPump=11). Unmappable values
    // are omitted rather than sent as an invalid enum value.
    if (baseType === 'Equipment' && fieldName === 'Asset category') {
      const normalized = String(value).toLowerCase().trim();
      const enumVal = ASSET_CATEGORY_MAP[normalized];
      if (enumVal !== undefined) {
        payload['assetCategory'] = enumVal;
      } else {
        const parsed = parseInt(value, 10);
        if (!isNaN(parsed) && parsed >= 0 && parsed <= 11) {
          payload['assetCategory'] = parsed;
        } else {
          console.warn(`[FMX Transform] Unsupported Asset Category "${value}" — field omitted`);
        }
      }
      return;
    }

    // Standard field
    const apiKey = fieldMap[fieldName];
    if (apiKey) {
      const fType = fieldTypeMap[fieldName];
      payload[apiKey] = fType ? coerceSystemFieldValue(value, fType) : value;
      return;
    }

    // If it's a lookup field, it'll be handled below — don't flag as dropped
    if (lookupFieldNames.has(fieldName)) return;

    // Field matched no path — track for logging
    droppedFields.push(fieldName);
  });

  if (droppedFields.length > 0) {
    console.warn(`[FMX Transform] ${droppedFields.length} unmapped field(s) for ${baseType}:`, droppedFields);
  }

  if (customFields.length > 0) {
    // Requisitions carry custom fields under `detailsCustomFields`.
    payload[getCustomFieldsPayloadKey(baseType)] = customFields;
  }

  // Compose scheduling-rate keys into the schedulingRates array.
  // apiKey format: `schedulingRates:<rateName>:<cost|unit>` (generated in App.js
  // from the Resource wizard's rate buckets). Parsed from both ends (prefix +
  // lastIndexOf) so rate names may contain ':'. Must run before the dot-notation
  // pass below: rate names may contain '.', and these keys are deleted here so
  // the dot pass never mangles them.
  const RATE_PREFIX = 'schedulingRates:';
  const rateKeys = Object.keys(payload).filter(k => k.startsWith(RATE_PREFIX));
  if (rateKeys.length > 0) {
    const ratesByName = new Map();
    for (const key of rateKeys) {
      const sep = key.lastIndexOf(':');
      const name = key.slice(RATE_PREFIX.length, sep);
      const part = key.slice(sep + 1); // 'cost' | 'unit'
      if (!ratesByName.has(name)) ratesByName.set(name, { name });
      ratesByName.get(name)[part] = payload[key];
      delete payload[key];
    }
    payload.schedulingRates = [...ratesByName.values()];
  }

  // Compose dot-notation keys into nested objects
  // e.g. 'schedule.frequency' → payload.schedule = { frequency: ... }
  // e.g. 'firstOccurrenceEventTimeBlock.startDate' → payload.firstOccurrenceEventTimeBlock = { startDate: ... }
  const dotKeys = Object.keys(payload).filter(k => k.includes('.'));
  for (const key of dotKeys) {
    const dotIdx = key.indexOf('.');
    const parent = key.slice(0, dotIdx);
    const child = key.slice(dotIdx + 1);
    if (!payload[parent] || typeof payload[parent] !== 'object') payload[parent] = {};
    payload[parent][child] = payload[key];
    delete payload[key];
  }

  // Resolve ID lookup fields (Building → buildingID, etc.)
  // For isArray fields, split delimited values (semicolons/commas) and resolve each individually.
  const lookups = effectiveLookups;
  Object.entries(lookups).forEach(([fmxField, lookup]) => {
    // Assignment lookups resolve into the ID cache (used by StepExport's
    // assignments chain) but never inject into the main payload.
    if (lookup.assignmentOnly) return;

    const rawValue = row[fmxField];
    if (!rawValue) return;

    if (lookup.isArray) {
      // Split multi-value input and resolve each individually
      const parts = String(rawValue).split(/[;,]/).map(s => s.trim()).filter(Boolean);
      const resolvedIds = [];
      for (const part of parts) {
        const key = `${fmxField}:${part}`;
        if (idCache[key]) {
          resolvedIds.push(idCache[key]);
        } else {
          console.warn(`[FMX ID Resolve] No match for "${fmxField}": "${part}" → ${lookup.idField}`);
        }
      }
      if (resolvedIds.length > 0) {
        if (lookup.idField === 'resourceQuantities') {
          payload[lookup.idField] = resolvedIds.map(id => ({ resourceID: id, quantity: 1 }));
        } else {
          payload[lookup.idField] = resolvedIds;
        }
      }
    } else {
      // Single-value lookup
      const cacheKey = `${fmxField}:${rawValue}`;
      if (idCache[cacheKey]) {
        payload[lookup.idField] = idCache[cacheKey];
      } else {
        console.warn(`[FMX ID Resolve] No match for "${fmxField}": "${rawValue}" → ${lookup.idField} will be missing from payload`);
      }
    }
  });

  if (baseType === 'User' && !payload.password) {
    payload.password = generateDefaultPassword();
  }

  PAYLOAD_FINALIZERS[baseType]?.(payload);

  return payload;
}

// Fetch all records from an endpoint using paginated requests.
// Used by update-mode to resolve existing entity IDs by name/tag. Thin adapter
// over fetchAllPages (which now fans pages out through the batch proxy); returns
// just the items array for the existing call sites.
// `creds` is { projectId } (preferred) or { siteUrl, email, password } (verify flows).
export async function fetchAllRecords(creds, endpoint, fields) {
  const { items } = await fetchAllPages(creds, endpoint, fields);
  return items;
}

// Resolves the {module} token in a lookup endpoint using the row's schemaType.
// No-op for endpoints without the token. If the token is present but the schemaType
// has no module slug (static type), logs a warning and returns the unresolved string
// so the bug surfaces as a 404 rather than silently hitting the wrong module.
function resolveLookupEndpoint(endpoint, schemaType) {
  if (!endpoint.includes('{module}')) return endpoint;
  const slug = getSchemaModuleSlug(schemaType);
  if (!slug) {
    console.warn(`[FMX lookup] {module} token in "${endpoint}" but schemaType "${schemaType}" has no module slug`);
    return endpoint;
  }
  return endpoint.replace('{module}', slug);
}

// Maps FMX API endpoints to dependency cache keys. Covers the static 1:1 deps;
// module-scoped deps (request-types, instruction-sets) are handled in
// resolveDepKey() below because they need the current schemaType's module slug.
const ENDPOINT_TO_DEP_KEY = {
  '/v1/buildings':        'buildings',
  '/v1/resources':        'resources',
  '/v1/users':            'users',
  '/v1/equipment-types':  'equipment-types',
  '/v1/equipment':        'equipment',
  '/v1/inventory-types':  'inventory-types',
  '/v1/inventory':        'inventory',
  '/v1/request-types':    'request-types',
  '/v1/resource-types':   'resource-types',
  '/v1/user-types':       'user-types',
};

// Picks the dep cache key to use when resolving a lookup.
// Returns an ordered list of candidate keys (preferred first). Callers should
// try each in turn and fall back to the next if the preferred cache is empty
// or missing — this keeps static behavior when dependencies haven't been
// re-synced against the new module-scoped schema yet.
//
// `resolvedEndpoint` is already post-{module}-substitution (see
// resolveLookupEndpoint). `schemaType` may be module-qualified
// (e.g. "Work Task:fit-inspections") or not.
function resolveDepKey(resolvedEndpoint, schemaType) {
  const slug = schemaType && schemaType.indexOf(':') !== -1
    ? schemaType.slice(schemaType.indexOf(':') + 1)
    : null;

  // Instruction-sets: path of the form /v1/{slug}/instruction-sets
  const instrMatch = resolvedEndpoint.match(/^\/v1\/([^/]+)\/instruction-sets$/);
  if (instrMatch) {
    const s = instrMatch[1];
    return [`work-task-instruction-sets:${s}`, 'work-task-instruction-sets'];
  }

  // Request types: module-scoped cache preferred, full list as fallback
  if (resolvedEndpoint === '/v1/request-types') {
    return slug ? [`request-types:${slug}`, 'request-types'] : ['request-types'];
  }

  const staticKey = ENDPOINT_TO_DEP_KEY[resolvedEndpoint];
  return staticKey ? [staticKey] : [];
}

// Build a name→ID lookup from dependency cache items.
function buildDepLookup(items, nameField = 'name') {
  const map = {};
  for (const item of items) {
    // `|| item.name` covers caches written before fmxSync stored non-name
    // fields (e.g. equipment `tag`) under their own key as well as `name`.
    const key = nameField === 'name' ? item.name : item[nameField] || item.name;
    if (key) map[key] = item.id;
  }
  return map;
}

// Match an input value against a dep lookup using case-insensitive + trimmed matching.
function matchDepLookup(value, depLookup) {
  if (depLookup[value] !== undefined) return depLookup[value];
  const lower = String(value).toLowerCase().trim();
  for (const [key, id] of Object.entries(depLookup)) {
    if (String(key).toLowerCase().trim() === lower) return id;
  }
  return undefined;
}

// Pre-fetch IDs for all unique reference values in the dataset.
// Returns { idCache: { "Building:Main Campus": 42, ... }, unresolved: [...] }
// If dependencyCaches is provided (from getAllDependencyCaches), uses cached name→ID mappings.
// Unresolved values are batched by endpoint and fetched in parallel bulk requests
// rather than issuing individual sequential API calls per value.
export async function buildIdCache(rows, schemaType, creds, dependencyCaches = [], lookupFieldsOverride = null) {
  const lookups = lookupFieldsOverride || {};
  const idCache = {};
  const unresolved = [];

  // Index dependency caches by key for quick access
  const depByKey = {};
  for (const row of dependencyCaches) {
    if (row.extra?.items) {
      depByKey[row.schema_type] = row.extra.items;
    }
  }

  // Phase 1: Collect unique values per field and resolve from dep cache.
  // Track unresolved values grouped by endpoint for bulk fetching.
  // pendingByEndpoint: { endpoint: [{ cacheKey, value, nameField }] }
  const pendingByEndpoint = {};

  for (const [fmxField, lookup] of Object.entries(lookups)) {
    const seen = new Set();
    const uniqueValues = [];
    for (const row of rows) {
      const cellValue = row[fmxField];
      if (!cellValue) continue;
      const vals = lookup.isArray
        ? String(cellValue).split(/[;,]/).map(s => s.trim()).filter(Boolean)
        : [String(cellValue)];
      for (const v of vals) {
        if (!seen.has(v)) { seen.add(v); uniqueValues.push(v); }
      }
    }

    const endpoint = resolveLookupEndpoint(lookup.endpoint, schemaType);

    // Try each candidate dep key in order; prefer a non-empty module-scoped
    // bucket, fall back to the consolidated cache if the bucket is missing
    // (e.g. deps haven't been re-synced since module-scoping landed).
    const depKeyCandidates = resolveDepKey(endpoint, schemaType);
    let depKey = null;
    let depItems = null;
    for (const cand of depKeyCandidates) {
      const items = depByKey[cand];
      if (items && items.length > 0) {
        depKey = cand;
        depItems = items;
        break;
      }
    }
    const baseKey = depKey ? depKey.split(':')[0] : null;
    const nameField = baseKey === 'equipment' ? 'tag' : 'name';
    const depLookup = depItems ? buildDepLookup(depItems, nameField) : {};

    for (const value of uniqueValues) {
      const cacheKey = `${fmxField}:${value}`;
      const depId = matchDepLookup(value, depLookup);
      if (depId !== undefined) {
        idCache[cacheKey] = depId;
        continue;
      }
      // Queue for bulk fetch
      if (!pendingByEndpoint[endpoint]) pendingByEndpoint[endpoint] = { nameField, items: [] };
      pendingByEndpoint[endpoint].items.push({ cacheKey, value });
    }
  }

  // Phase 2: Bulk-fetch all records for each endpoint with unresolved values.
  // Uses fetchAllPages with minimal fields to get a complete name→ID map per endpoint,
  // then matches locally — replaces N sequential search calls with one paginated fetch.
  const endpointEntries = Object.entries(pendingByEndpoint);
  if (endpointEntries.length > 0) {
    const settled = await Promise.allSettled(
      endpointEntries.map(async ([endpoint, { nameField, items }]) => {
        // Fetch all records from this endpoint with minimal fields
        const fields = `id,name,tag,email`;
        try {
          const { items: allRecords } = await fetchAllPages(creds, endpoint, fields);
          const bulkLookup = buildDepLookup(allRecords, nameField);
          // Also build a secondary lookup by 'name' if nameField is 'tag' (or vice versa)
          // to catch matches on either field
          const altLookup = nameField !== 'name'
            ? buildDepLookup(allRecords, 'name')
            : buildDepLookup(allRecords, 'tag');

          for (const { cacheKey, value } of items) {
            const id = matchDepLookup(value, bulkLookup) ?? matchDepLookup(value, altLookup);
            if (id !== undefined) {
              idCache[cacheKey] = id;
            } else {
              unresolved.push(cacheKey);
            }
          }
        } catch (e) {
          console.warn(`[buildIdCache] bulk fetch failed for ${endpoint}, falling back to individual lookups:`, e);
          // Fallback: individual search calls (original behavior)
          for (const { cacheKey, value } of items) {
            try {
              const res = await fmxFetch({
                ...creds,
                endpoint: `${endpoint}?search=${encodeURIComponent(value)}&limit=1&fields=id,name,tag`,
                method: 'GET',
              });
              const data = await res.json();
              const records = Array.isArray(data) ? data : (data.items || data.data || data.results || []);
              if (records.length > 0) {
                idCache[cacheKey] = records[0].id;
              } else {
                unresolved.push(cacheKey);
              }
            } catch (err) {
              console.warn(`Could not resolve ID for ${cacheKey}`, err);
              unresolved.push(cacheKey);
            }
          }
        }
      })
    );
    // Log any rejected promises (shouldn't happen since we catch inside, but safety net)
    for (const r of settled) {
      if (r.status === 'rejected') console.warn('[buildIdCache] unexpected rejection:', r.reason);
    }
  }

  return { idCache, unresolved };
}
